import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import {
  ImportJobDto,
  ImportJobStatus,
} from '../../../models/user/dto/import-job.dto';

const TERMINAL: ImportJobStatus[] = [
  ImportJobStatus.COMPLETED,
  ImportJobStatus.PARTIAL,
  ImportJobStatus.FAILED,
  ImportJobStatus.ROLLED_BACK,
  ImportJobStatus.CANCELED,
];

export const isTerminalImportStatus = (status?: ImportJobStatus): boolean =>
  !!status && TERMINAL.includes(status);

/**
 * READY is not terminal, but nothing will change until the operator confirms or
 * cancels — so polling it would just burn requests while the modal waits on a
 * human. Treat it as a rest state for the poller.
 */
const isRestingImportStatus = (status?: ImportJobStatus): boolean =>
  isTerminalImportStatus(status) || status === ImportJobStatus.READY;

export const getImportJobQueryKey = (organizationId: string, jobId?: string) => [
  'import-job',
  organizationId,
  jobId,
];

/**
 * Polls one import job every 2s until it reaches a terminal state (or parks at
 * READY awaiting the operator), then stops. Enabled once a jobId exists.
 *
 * NOTE: because READY stops the poller, whatever moves a job OUT of READY is
 * responsible for restarting it — see useConfirmImportJob.
 */
export const useGetImportJob = (organizationId: string, jobId?: string) => {
  const client = useApiClient();

  return useQuery({
    queryKey: getImportJobQueryKey(organizationId, jobId),
    enabled: !!jobId,
    refetchInterval: (data) =>
      data && isRestingImportStatus(data.status) ? false : 2000,
    queryFn: () =>
      client.fetchApi<ImportJobDto, undefined>({
        endpoint: `users/imports/${jobId}?organizationId=${organizationId}`,
        method: 'GET',
      }),
  });
};
